"use client";

import { useTranslations } from "next-intl";
import { useLayoutEffect, useRef, useState, type ComponentProps, type Ref } from "react";

import { Icon } from "@/components/icons";

type Selection = { start: number | null; end: number | null };

// A password field with a show / hide toggle (the eye) inside its right edge.
// Takes the native <input> props (minus `type`) and forwards them unchanged, so
// password managers and autofill see the same field. It always starts hidden
// and never remembers the choice; unmounting resets it.
//
// The toggle doesn't take focus on a mouse click (onMouseDown prevents it), so
// typing continues in the field, and the caret / selection survives the type
// switch. From the keyboard it's a normal button (Tab, Space / Enter), and
// type="button" means it never submits the form.
export function PasswordInput({
  className,
  ref,
  ...props
}: Omit<ComponentProps<"input">, "type">) {
  const t = useTranslations("common");
  const [visible, setVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const selection = useRef<Selection | null>(null);

  // Changing `type` can move the caret to the start: put it back. Chrome does
  // it after the next layout, so restore once more on the next frame.
  useLayoutEffect(() => {
    const input = inputRef.current;
    const saved = selection.current;
    selection.current = null;
    if (!input || !saved) return;
    const restore = () => {
      if (document.activeElement === input) input.setSelectionRange(saved.start, saved.end);
    };
    restore();
    const frame = requestAnimationFrame(restore);
    return () => cancelAnimationFrame(frame);
  }, [visible]);

  function setRefs(node: HTMLInputElement | null) {
    inputRef.current = node;
    assignRef(ref, node);
  }

  function toggle() {
    const input = inputRef.current;
    if (input && document.activeElement === input) {
      selection.current = { start: input.selectionStart, end: input.selectionEnd };
    }
    setVisible((v) => !v);
  }

  return (
    <div className="relative flex flex-col">
      <input
        {...props}
        ref={setRefs}
        type={visible ? "text" : "password"}
        className={`${className ?? ""} pr-10 [&::-ms-clear]:hidden [&::-ms-reveal]:hidden`}
      />
      <button
        type="button"
        onClick={toggle}
        onMouseDown={(e) => e.preventDefault()}
        aria-label={visible ? t("hidePassword") : t("showPassword")}
        aria-pressed={visible}
        aria-controls={props.id}
        disabled={props.disabled}
        className="absolute inset-y-0 right-1 my-auto inline-flex size-8 items-center justify-center rounded-md text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:text-foreground disabled:opacity-60"
      >
        <Icon name={visible ? "eyeOff" : "eye"} size={18} />
      </button>
    </div>
  );
}

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (typeof ref === "function") ref(value);
  else if (ref) ref.current = value;
}
